/**
 * BE-201 — the UI language and the currency are the account's (`profile-data.user.language` /
 * `.currency`), written with `PATCH /user/profile`. The browser keeps the last applied value
 * (`userLocale` / `currency`) only as the GUEST value and the request headers' source before the
 * profile answers; once signed in, the account wins on every device. Behind `profile_locale`.
 */
import { watch, type Ref } from 'vue'
import { patchUserProfile } from '@/features/auth/api/profile'
import { hasSurveysApi } from '@/features/survey/flags'
import { queryCache } from '@/queries'
import { useLocaleStore, type AvailableLocale } from '@/shared/store/useLocaleStore'
import { availableLocales } from '@/locales'
import { persistCurrency, readStoredCurrency } from '@/ui/tokens/fair-locale-menu.data'

/** What applying the account's values changes; pure, so the rule is tested without a store. */
export function accountLocaleDiff(
  account: { language?: string | null; currency?: string | null } | null | undefined,
  current: { language: string; currency: string },
  shipped: readonly string[],
): { language: string | null; currency: string | null } {
  const language = account?.language && shipped.includes(account.language) && account.language !== current.language
    ? account.language
    : null
  const code = account?.currency?.toUpperCase() ?? null
  const currency = code && code !== current.currency.toUpperCase() ? code : null
  return { language, currency }
}

function patch(body: { language?: string; currency?: string }) {
  patchUserProfile(body)
    .then(() => queryCache.invalidate((key) => Array.isArray(key) && key[0] === 'user-profile'))
    .catch((error) => console.warn('Locale: not saved to the account', error))
}

/* ── Decision 19: the guest's choice moves to the account ONCE, at sign-in ── */

/** What the guest picked in the menus (only what they picked), until the first sign-in. */
export const GUEST_LOCALE_CHOICE_KEY = 'fair.guestLocaleChoice'

export interface GuestLocaleChoice {
  language?: string
  currency?: string
}

function readGuestLocaleChoice(): GuestLocaleChoice {
  if (typeof window === 'undefined') return {}
  try {
    const raw = JSON.parse(window.localStorage.getItem(GUEST_LOCALE_CHOICE_KEY) ?? '{}') as Record<string, unknown>
    return {
      ...(typeof raw.language === 'string' ? { language: raw.language } : {}),
      ...(typeof raw.currency === 'string' ? { currency: raw.currency } : {}),
    }
  } catch {
    return {}
  }
}

function recordGuestLocaleChoice(patchValue: GuestLocaleChoice) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(GUEST_LOCALE_CHOICE_KEY, JSON.stringify({ ...readGuestLocaleChoice(), ...patchValue }))
  } catch {
    // Private mode: the account keeps its own values.
  }
}

/** A guest choice waits for the sign-in transfer: the account's older values must not overwrite it. */
export function hasPendingGuestLocaleChoice(): boolean {
  const choice = readGuestLocaleChoice()
  return Boolean(choice.language || choice.currency)
}

/**
 * The `PATCH /user/profile` body for the transfer: only what the guest chose, only values the
 * API accepts (`language ∈ en | ru` — the menu offers more, T-021; `currency` upper-case).
 * `null` = nothing to send. Pure.
 */
export function guestLocaleTransferBody(
  choice: GuestLocaleChoice,
  apiLanguages: readonly string[],
): { language?: string; currency?: string } | null {
  const body: { language?: string; currency?: string } = {}
  if (choice.language && apiLanguages.includes(choice.language)) body.language = choice.language
  if (choice.currency && /^[A-Za-z]{3}$/.test(choice.currency)) body.currency = choice.currency.toUpperCase()
  return body.language || body.currency ? body : null
}

/** `PATCH /user/profile {language, currency}` accepts these (BE-201). */
export const ACCOUNT_API_LANGUAGES: readonly string[] = ['en', 'ru']

/**
 * Right after the first token of a sign-in: one PATCH with the guest's choice, then the browser
 * copy of the CHOICE is dropped (the applied `userLocale` / `currency` stay as request headers).
 */
export async function transferGuestLocaleChoice(): Promise<void> {
  if (!hasSurveysApi('profile_locale')) return
  const body = guestLocaleTransferBody(readGuestLocaleChoice(), ACCOUNT_API_LANGUAGES)
  try {
    if (body) {
      await patchUserProfile(body)
    }
  } catch (error) {
    console.warn('Locale: the guest choice was not moved to the account', error)
  }
  try {
    window.localStorage.removeItem(GUEST_LOCALE_CHOICE_KEY)
  } catch {
    // nothing to drop
  }
  // Re-read after the marker is gone, so the sync applies the account (now = the choice).
  queryCache.invalidate((key) => Array.isArray(key) && key[0] === 'user-profile')
}

/** The menus call these after applying the value locally. A guest's choice waits for sign-in. */
export function saveAccountLanguage(language: string, signedIn: boolean) {
  if (!signedIn) {
    recordGuestLocaleChoice({ language })
    return
  }
  if (!hasSurveysApi('profile_locale')) return
  if (!(availableLocales as readonly string[]).includes(language)) return
  patch({ language })
}

export function saveAccountCurrency(currency: string, signedIn: boolean) {
  if (!signedIn) {
    recordGuestLocaleChoice({ currency: currency.toUpperCase() })
    return
  }
  if (!hasSurveysApi('profile_locale')) return
  patch({ currency: currency.toUpperCase() })
}

/** App shell: applies the signed-in account's language / currency when profile-data arrives. */
export function useAccountLocaleSync(user: Ref<{ language?: string | null; currency?: string | null } | null | undefined>) {
  const localeStore = useLocaleStore()
  watch(
    user,
    (account) => {
      if (!account || !hasSurveysApi('profile_locale')) return
      // Decision 19: the sign-in transfer is on its way; the re-read profile carries the choice.
      if (hasPendingGuestLocaleChoice()) return
      const diff = accountLocaleDiff(
        account,
        { language: localeStore.currentLocale, currency: readStoredCurrency() },
        availableLocales,
      )
      if (diff.language) localeStore.setLocale(diff.language as AvailableLocale)
      if (diff.currency) {
        persistCurrency(diff.currency)
        // Prices of the cached pages were asked in the old currency.
        queryCache.invalidate(() => true)
      }
    },
    { immediate: true },
  )
}
